import React from "react"
import { makeAutoObservable } from "mobx"
import { t } from "@lingui/macro"

import { FormFields } from "src/lib/form-fields"
import { reportError } from "src/lib/report"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import { ISelectedAccessGroup } from "src/types/local-storage"
import {
    IKnowledgeItemFormFields,
    IKnowledgeItemRequestData,
} from "src/modals/knowledge-items/types/formFields"

import {
    ChatbotAdminService,
    KnowledgeHubAdminService,
    config_ConfigDefinitionItem,
} from "src/api"
import { IGenericAutoCompleteTextFieldValue } from "src/types/auto-complete-text-field"
import { EModalMode } from "src/types/modal/modal"
import { createLoadingKeys } from "src/lib/loading"
import { loads } from "src/channel/utils"

export class KnowledgeItemStore {
    static Context = React.createContext<KnowledgeItemStore | null>(null)
    static LoadingKeys = createLoadingKeys("init", "loading")

    formFields = new FormFields<IKnowledgeItemFormFields>({})
    isLoading = false
    accessType: string = ""
    accessGroups: ISelectedAccessGroup[] = []
    accessGroupId: number = DEFAULT_ACCESS_GROUP.id
    isEditMode = false
    agentAccessTypes: IGenericAutoCompleteTextFieldValue[] = []
    agentDefinitions: config_ConfigDefinitionItem[] = []
    agentInstances: Map<string, IGenericAutoCompleteTextFieldValue[]> =
        new Map()
    isLoadingAgentTypes = false
    createdItemId?: number

    private id: number | null = null

    constructor() {
        makeAutoObservable(this)
    }

    // #region getters
    get isReadOnly() {
        return this.accessType === "READ"
    }

    get ID() {
        return this.id
    }

    get hasAgentInstances() {
        const agentInstances = this.formFields.get("agent_instances")
        return Boolean(
            agentInstances !== null &&
                agentInstances !== undefined &&
                Object.keys(agentInstances).length > 0,
        )
    }
    // #endregion

    // #region setters
    setAgentAccessTypes = (types: IGenericAutoCompleteTextFieldValue[]) => {
        this.agentAccessTypes = types
    }

    setAgentDefinitions = (definitions: config_ConfigDefinitionItem[]) => {
        this.agentDefinitions = definitions
    }

    setAgentInstances = (
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ) => {
        this.agentInstances.set(agentName, instances)
    }

    setIsLoadingAgentTypes = (isLoading: boolean) => {
        this.isLoadingAgentTypes = isLoading
    }

    setAccessGroups = (accessGroups: ISelectedAccessGroup[]) => {
        this.accessGroups = accessGroups
    }

    setSelectedAccessGroupId = (accessGroupId: number) => {
        this.accessGroupId = accessGroupId
    }

    setIsLoading = (isLoading: boolean) => (this.isLoading = isLoading)

    setAccessType = (accessType: string) => {
        this.accessType = accessType
    }

    init = async (
        accessGroupId: number,
        id?: number,
        mode: EModalMode = EModalMode.Edit,
        preselectedValues?: {
            agentAccess?: string
            knowledgeType?: string
            segments?: number[]
        },
    ) => {
        try {
            const isEditMode = id !== undefined
            let existingConfigTypeIds: number[] = []
            let existingFeatureConfigIds: number[] = []

            if (isEditMode) {
                const knowledgeItem =
                    await KnowledgeHubAdminService.getV1AdminKnowledgebaseKnowledgeItems(
                        {
                            knowledgeItemId: id,
                        },
                    )
                const searchInstructions =
                    knowledgeItem.search_instructions ?? ""
                const isFullSiteSearch =
                    this.parseSearchScopeFromInstructions(searchInstructions)
                const cleanSearchInstructions =
                    this.removeSearchScopeFromInstructions(searchInstructions)

                // Initialize form fields
                this.formFields.init({
                    title: knowledgeItem.title,
                    knowledge_item_type: knowledgeItem.knowledge_item_type,
                    access_type: knowledgeItem.access_type,
                    access_group_id:
                        knowledgeItem.access_group_id ?? accessGroupId,
                    content: knowledgeItem.content,
                    search_instructions: cleanSearchInstructions,
                    is_full_site_search: isFullSiteSearch,
                    tags: knowledgeItem.tags,
                    segments: knowledgeItem.segments,
                    agents: knowledgeItem.config_type_ids ?? [],
                    agent_instances: {}, // Will be populated after fetching
                })

                // Store the existing configuration data for later processing
                existingConfigTypeIds = knowledgeItem.config_type_ids ?? []
                existingFeatureConfigIds =
                    knowledgeItem.feature_config_ids ?? []

                if (mode === EModalMode.Copy) {
                    // For copy mode, reset access group but keep segments visible
                    // so user can see what was originally published and modify if needed
                    this.formFields.set("access_group_id", accessGroupId)
                    // Don't set the ID so it creates a new item
                } else {
                    this.setId(
                        knowledgeItem.id !== null &&
                            knowledgeItem.id !== undefined
                            ? knowledgeItem.id
                            : 0,
                    )
                }

                this.setAccessType(knowledgeItem.access_type ?? "WRITE")
            } else {
                this.formFields.set("access_group_id", accessGroupId)
                this.formFields.set("agent_instances", {})

                // Apply preselected values if provided
                if (
                    preselectedValues !== null &&
                    preselectedValues !== undefined
                ) {
                    if (
                        preselectedValues.knowledgeType !== null &&
                        preselectedValues.knowledgeType !== undefined
                    ) {
                        this.formFields.set(
                            "knowledge_item_type",
                            preselectedValues.knowledgeType as
                                | "page"
                                | "file"
                                | "web",
                        )
                    }
                    if (
                        preselectedValues.segments !== null &&
                        preselectedValues.segments !== undefined
                    ) {
                        this.formFields.set(
                            "segments",
                            preselectedValues.segments,
                        )
                    }
                }
            }

            // Load agent types
            await this.fetchAgentTypes()

            // Apply agent access preselection after agent types are loaded
            if (
                preselectedValues !== null &&
                preselectedValues !== undefined &&
                preselectedValues.agentAccess !== null &&
                preselectedValues.agentAccess !== undefined &&
                !isEditMode
            ) {
                // Find the agent type that matches "qa_agent"
                const qaAgentType = this.agentAccessTypes.find(
                    (agent) =>
                        agent.label.toLowerCase().includes("qa") ||
                        agent.label.toLowerCase().includes("qa_agent"),
                )
                if (qaAgentType !== null && qaAgentType !== undefined) {
                    this.formFields.set("agents", [Number(qaAgentType.id)])

                    // Also preselect "make available to all instances" for qa_agent
                    const agentInstances =
                        this.formFields.get("agent_instances") ?? {}
                    agentInstances[qaAgentType.label] = [-1]
                    this.formFields.set("agent_instances", agentInstances)
                }
            }

            // If editing, fetch instances for ALL agent types so we can match feature_config_ids
            if (isEditMode) {
                await this.fetchAllAgentInstances()

                // Reverse-engineer agent types from existing configuration
                this.reconstructAgentTypesFromExistingConfig(
                    existingConfigTypeIds,
                    existingFeatureConfigIds,
                )

                // Now check if we have agents after reconstruction
                const agentsFromForm = this.formFields.get("agents")

                if (
                    agentsFromForm !== null &&
                    agentsFromForm !== undefined &&
                    agentsFromForm.length > 0
                ) {
                    const currentAgents = this.getAgentTypeValue()
                    await this.handleAgentSelection(currentAgents)

                    // Process existing configuration after instances are fetched
                    this.processExistingAgentConfiguration(
                        currentAgents,
                        existingConfigTypeIds,
                        existingFeatureConfigIds,
                    )
                }
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-data-fail`, error)
            this.setAccessType("READ")
        }
    }

    // Process existing agent configuration after instances have been fetched
    processExistingAgentConfiguration = (
        currentAgents: IGenericAutoCompleteTextFieldValue[],
        existingConfigTypeIds: number[],
        existingFeatureConfigIds: number[],
    ) => {
        const updatedAgentInstances: Record<string, number[]> = {}

        for (const agent of currentAgents) {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agent.id,
            )

            if (
                agentDefinition !== null &&
                agentDefinition !== undefined &&
                agentDefinition.name !== null &&
                agentDefinition.name !== undefined &&
                agentDefinition.name.length > 0
            ) {
                const agentTypeId =
                    agentDefinition.config_definition_id !== null &&
                    agentDefinition.config_definition_id !== undefined
                        ? agentDefinition.config_definition_id
                        : 0
                const agentName = agentDefinition.name

                // Check if this agent type is configured for "all instances"
                if (existingConfigTypeIds.includes(agentTypeId)) {
                    // Agent type is enabled for all instances
                    updatedAgentInstances[agentName] = [-1]
                } else {
                    // Check if specific instances are configured for this agent type
                    // We need to get the instances for this agent to match IDs
                    const availableInstances =
                        this.getInstancesForAgentType(agentName)

                    const configuredInstanceIds =
                        existingFeatureConfigIds.filter((instanceId: number) =>
                            availableInstances.some(
                                (instance) => instance.id === instanceId,
                            ),
                        )

                    if (configuredInstanceIds.length > 0) {
                        // Specific instances are configured
                        updatedAgentInstances[agentName] = configuredInstanceIds
                    } else {
                        // No specific configuration found, default based on requirements
                        const requiresInstances = Boolean(
                            (
                                agentDefinition.metadata as Record<
                                    string,
                                    unknown
                                >
                            )?.agent_instance_required,
                        )

                        if (!requiresInstances) {
                            // For agents that don't require instances, default to enabled
                            updatedAgentInstances[agentName] = [-1]
                        }
                        // For agents that require instances, leave empty (user must select)
                    }
                }
            }
        }

        this.formFields.set("agent_instances", updatedAgentInstances)
    }

    // #endregion

    fetchAgentTypes = async () => {
        try {
            this.setIsLoadingAgentTypes(true)
            const configDefinition =
                await ChatbotAdminService.getV1AdminChatbotAgentConfigConfigDefinition(
                    {
                        space: "knowledgebase",
                    },
                )

            if (
                configDefinition.definition !== null &&
                configDefinition.definition !== undefined &&
                configDefinition.definition.length > 0
            ) {
                // Store the full definitions for metadata access
                this.setAgentDefinitions(configDefinition.definition)

                const types = configDefinition.definition
                    .map((field) => ({
                        id:
                            field.config_definition_id !== null &&
                            field.config_definition_id !== undefined
                                ? field.config_definition_id
                                : 0,
                        label: field.name ?? "",
                    }))
                    .filter(
                        (type) =>
                            type.id !== null &&
                            type.id !== undefined &&
                            type.id > 0,
                    )

                this.setAgentAccessTypes(types)
            }
        } catch (error) {
            reportError(t`knowledge-item-modal.load-agent-types-fail`, error)
        } finally {
            this.setIsLoadingAgentTypes(false)
        }
    }

    fetchAgentInstances = async (agentName: string) => {
        try {
            const response =
                await KnowledgeHubAdminService.getV1AdminKnowledgebaseKnowledgeItemsConnectableAgents(
                    {
                        agentType: agentName,
                    },
                )

            const instances = (response.configs ?? []).map((config) => ({
                id:
                    config.feature_config_id !== null &&
                    config.feature_config_id !== undefined
                        ? config.feature_config_id
                        : 0,
                label: config.name ?? "",
            }))

            this.setAgentInstances(agentName, instances)
        } catch (error) {
            reportError(
                t`knowledge-item-modal.load-agent-instances-fail`,
                error,
            )
        }
    }

    fetchAllAgentInstances = async () => {
        // Fetch instances for all agent types so we can match feature_config_ids
        const promises = this.agentDefinitions.map(async (definition) => {
            if (
                definition.name !== null &&
                definition.name !== undefined &&
                definition.name.length > 0
            ) {
                await this.fetchAgentInstances(definition.name)
            }
        })
        await Promise.all(promises)
    }

    reconstructAgentTypesFromExistingConfig = (
        existingConfigTypeIds: number[],
        existingFeatureConfigIds: number[],
    ) => {
        // Find agent types that match the existing config type IDs
        const reconstructedAgentTypeIds: number[] = []

        // Add config type IDs that match our agent definitions
        for (const configTypeId of existingConfigTypeIds) {
            const matchingDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === configTypeId,
            )
            if (
                matchingDefinition !== null &&
                matchingDefinition !== undefined
            ) {
                reconstructedAgentTypeIds.push(configTypeId)
            }
        }

        // Also find agent types from feature config IDs (instances)
        for (const featureConfigId of existingFeatureConfigIds) {
            // Find which agent type this instance belongs to
            for (const [agentName, instances] of this.agentInstances) {
                const instanceExists = instances.some(
                    (instance) => instance.id === featureConfigId,
                )
                if (instanceExists) {
                    const agentDefinition = this.agentDefinitions.find(
                        (def) => def.name === agentName,
                    )
                    if (
                        agentDefinition !== null &&
                        agentDefinition !== undefined &&
                        agentDefinition.config_definition_id !== null &&
                        agentDefinition.config_definition_id !== undefined &&
                        !reconstructedAgentTypeIds.includes(
                            agentDefinition.config_definition_id,
                        )
                    ) {
                        reconstructedAgentTypeIds.push(
                            agentDefinition.config_definition_id,
                        )
                    }
                }
            }
        }

        // Update the agents field with reconstructed data
        this.formFields.set("agents", reconstructedAgentTypeIds)
    }

    getSelectedAgentTypesForInstances = (): config_ConfigDefinitionItem[] => {
        const selectedAgentIds = this.formFields.get("agents") ?? []
        return this.agentDefinitions.filter((definition) =>
            selectedAgentIds.includes(
                definition.config_definition_id !== null &&
                    definition.config_definition_id !== undefined
                    ? definition.config_definition_id
                    : 0,
            ),
        )
    }

    hasExistingInstanceConfigurations = (): boolean => {
        const agentInstances = this.formFields.get("agent_instances")
        return Boolean(
            agentInstances !== null &&
                agentInstances !== undefined &&
                Object.keys(agentInstances).length > 0,
        )
    }

    getInstancesForAgentType = (
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] => {
        return this.agentInstances.get(agentName) ?? []
    }

    getSelectedInstancesForAgentType = (
        agentName: string,
    ): IGenericAutoCompleteTextFieldValue[] => {
        const agentInstances = this.formFields.get("agent_instances")
        if (
            agentInstances === null ||
            agentInstances === undefined ||
            agentInstances[agentName] === undefined
        ) {
            return []
        }

        const selectedInstanceIds = agentInstances[agentName]
        const availableInstances = this.getInstancesForAgentType(agentName)

        return selectedInstanceIds
            .map((id) => {
                // Handle special case for "all instances" marker
                if (id === -1) {
                    return { id: -1, label: "all-instances" }
                }
                return availableInstances.find((instance) => instance.id === id)
            })
            .filter(
                (instance): instance is IGenericAutoCompleteTextFieldValue =>
                    instance !== null && instance !== undefined,
            )
    }

    setSelectedInstancesForAgentType = (
        agentName: string,
        instances: IGenericAutoCompleteTextFieldValue[],
    ) => {
        const currentAgentInstances =
            this.formFields.get("agent_instances") ?? {}
        const instanceIds = instances.map((instance) => instance.id as number)

        this.formFields.set("agent_instances", {
            ...currentAgentInstances,
            [agentName]: instanceIds,
        })
    }

    handleAgentSelection = async (
        selectedAgents: IGenericAutoCompleteTextFieldValue[],
    ) => {
        // Store selected agent types
        const agentIds = selectedAgents.map((agent) => agent.id as number)
        this.formFields.set("agents", agentIds)

        // Fetch instances for newly selected agent types
        const promises = selectedAgents.map(async (agent) => {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agent.id,
            )

            if (
                agentDefinition !== null &&
                agentDefinition !== undefined &&
                agentDefinition.name !== null &&
                agentDefinition.name !== undefined &&
                agentDefinition.name.length > 0
            ) {
                // Only fetch if we don't already have instances for this agent
                if (!this.agentInstances.has(agentDefinition.name)) {
                    await this.fetchAgentInstances(agentDefinition.name)
                }

                // For agents that don't require instances, default to "all instances" enabled
                const requiresInstances = Boolean(
                    (agentDefinition.metadata as Record<string, unknown>)
                        ?.agent_instance_required,
                )

                if (!requiresInstances) {
                    const currentAgentInstances =
                        this.formFields.get("agent_instances") ?? {}
                    if (
                        currentAgentInstances[agentDefinition.name] ===
                        undefined
                    ) {
                        // Default to "all instances" enabled
                        this.formFields.set("agent_instances", {
                            ...currentAgentInstances,
                            [agentDefinition.name]: [-1],
                        })
                    }
                }
            }
        })

        await Promise.all(promises)

        // Clean up instances for unselected agent types
        this.cleanupUnselectedAgentInstances(selectedAgents)
    }

    cleanupUnselectedAgentInstances = (
        selectedAgents: IGenericAutoCompleteTextFieldValue[],
    ) => {
        const currentAgentInstances =
            this.formFields.get("agent_instances") ?? {}
        const updatedAgentInstances: Record<string, number[]> = {}

        // Keep only instances for selected agent types
        selectedAgents.forEach((agent) => {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agent.id,
            )

            if (
                agentDefinition !== null &&
                agentDefinition !== undefined &&
                agentDefinition.name !== null &&
                agentDefinition.name !== undefined &&
                agentDefinition.name.length > 0 &&
                currentAgentInstances[agentDefinition.name] !== undefined
            ) {
                updatedAgentInstances[agentDefinition.name] =
                    currentAgentInstances[agentDefinition.name]
            }
        })

        this.formFields.set("agent_instances", updatedAgentInstances)
    }

    getAllAgentOptions = (): IGenericAutoCompleteTextFieldValue[] => {
        return this.agentAccessTypes
    }

    validateRequiredAgentInstances = (): string | null => {
        const selectedAgents = this.getAgentTypeValue()
        const agentInstances = this.formFields.get("agent_instances") ?? {}

        for (const agent of selectedAgents) {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agent.id,
            )

            if (
                agentDefinition !== null &&
                agentDefinition !== undefined &&
                agentDefinition.name !== null &&
                agentDefinition.name !== undefined
            ) {
                const requiresInstances = Boolean(
                    (agentDefinition.metadata as Record<string, unknown>)
                        ?.agent_instance_required,
                )

                if (requiresInstances) {
                    const selectedInstances =
                        agentInstances[agentDefinition.name]
                    if (
                        selectedInstances === null ||
                        selectedInstances === undefined ||
                        selectedInstances.length === 0
                    ) {
                        return t`knowledge-item-modal.agent-instances-required`
                    }
                }
            }
        }

        return null
    }

    shouldShowPublishSettings = (): boolean => {
        // Show publish settings if no agents are selected OR if at least one selected agent doesn't require instances
        const selectedAgents = this.getAgentTypeValue()

        if (selectedAgents.length === 0) {
            return true // Show when no agents selected
        }

        // Show publish settings when at least one selected agent has agent_instance_required as false or unknown
        return selectedAgents.some((agent) => {
            const agentDefinition = this.agentDefinitions.find(
                (def) => def.config_definition_id === agent.id,
            )

            if (agentDefinition !== null && agentDefinition !== undefined) {
                const agentInstanceRequired = (
                    agentDefinition.metadata as Record<string, unknown>
                )?.agent_instance_required

                // Show when agent_instance_required is false or unknown (undefined)
                return !Boolean(agentInstanceRequired)
            }

            // Default to showing if we can't find the definition (unknown case)
            return true
        })
    }

    publishRelationsRequest = async (
        data: IKnowledgeItemRequestData,
        id?: number,
    ) => {
        // Apply segments (including empty arrays to clear connections)
        if (
            data.segments !== null &&
            data.segments !== undefined &&
            id !== null &&
            id !== undefined
        ) {
            await KnowledgeHubAdminService.putV1AdminKnowledgebaseKnowledgeItemsPublish(
                {
                    knowledgeItemId: id,
                    request: {
                        published_in: data.segments,
                    },
                },
            )
        }

        // Apply tags (including empty arrays to clear connections)
        if (
            data.tags !== null &&
            data.tags !== undefined &&
            id !== null &&
            id !== undefined
        ) {
            await KnowledgeHubAdminService.putV1AdminKnowledgebaseKnowledgeItemsTag(
                {
                    knowledgeItemId: id,
                    request: {
                        tag_ids: data.tags,
                    },
                },
            )
        }

        // Apply agents (including empty arrays to clear connections)
        if (
            data.agents !== null &&
            data.agents !== undefined &&
            id !== null &&
            id !== undefined
        ) {
            const agentInstances = data.agent_instances ?? {}

            // Separate agent types into two categories
            const configTypeIds: number[] = []
            const configIds: number[] = []

            for (const agentId of data.agents) {
                const agentDefinition = this.agentDefinitions.find(
                    (def) => def.config_definition_id === agentId,
                )

                if (
                    agentDefinition !== null &&
                    agentDefinition !== undefined &&
                    agentDefinition.name !== null &&
                    agentDefinition.name !== undefined
                ) {
                    const agentName = agentDefinition.name
                    const selectedInstances = agentInstances[agentName]

                    if (
                        selectedInstances !== null &&
                        selectedInstances !== undefined &&
                        selectedInstances.length > 0
                    ) {
                        // Check if "all instances" is enabled (marked with -1)
                        const hasAllInstancesMarker =
                            selectedInstances.includes(-1)

                        if (hasAllInstancesMarker) {
                            // Send only config_type_ids (agent type) for "all instances"
                            configTypeIds.push(agentId)
                        } else {
                            // Send only config_ids (specific instances)
                            configIds.push(...selectedInstances)
                        }
                    }
                }
            }

            // Always send the request, even with empty arrays to clear connections
            await KnowledgeHubAdminService.putV1AdminKnowledgebaseKnowledgeItemsConnectAgents(
                {
                    knowledgeItemId: id,
                    request: {
                        config_type_ids: configTypeIds,
                        config_ids: configIds,
                    },
                },
            )
        }
    }

    @loads(() => KnowledgeItemStore.LoadingKeys.init)
    async createItem() {
        const data = this.getRequestData()

        const response =
            await KnowledgeHubAdminService.postV1AdminKnowledgebaseKnowledgeItems(
                {
                    request: {
                        title: data.title ?? "",
                        knowledge_item_type: data.knowledge_item_type ?? "page",
                        access_group_id: data.access_group_id ?? 0,
                        content:
                            typeof data.content === "string"
                                ? data.content
                                : "",
                        search_instructions: data.search_instructions,
                    },
                },
            )

        if (
            response !== null &&
            response !== undefined &&
            response.id !== null &&
            response.id !== undefined
        ) {
            this.createdItemId = response.id
            await this.publishRelationsRequest(data, response.id)
        }
    }

    @loads(() => KnowledgeItemStore.LoadingKeys.init)
    async updateItem() {
        if (this.id === null || this.id === undefined) {
            throw new Error("Cannot update item without ID")
        }

        const data = this.getRequestData()

        await KnowledgeHubAdminService.putV1AdminKnowledgebaseKnowledgeItems({
            knowledgeItemId: this.id ?? 0,
            request: {
                title: data.title ?? "",
                access_group_id: data.access_group_id ?? 0,
                content: typeof data.content === "string" ? data.content : "",
                search_instructions: data.search_instructions,
            },
        })

        await this.publishRelationsRequest(data, this.id)
    }

    submit = async (): Promise<boolean> => {
        try {
            // Validate required agent instances
            const instanceValidationError =
                this.validateRequiredAgentInstances()
            if (instanceValidationError !== null) {
                this.formFields.setError("agents", instanceValidationError)
                return false
            }

            if (this.id !== null && this.id !== undefined) {
                await this.updateItem()
            } else {
                await this.createItem()
            }

            return true
        } catch (error) {
            reportError(t`knowledge-item-modal.save-item-fail`, error)
            return false
        } finally {
            this.setIsLoading(false)
        }
    }

    getRequestData = (): IKnowledgeItemRequestData => {
        const searchInstructions = this.formFields.get("search_instructions")
        const isFullSiteSearch = this.formFields.get("is_full_site_search")

        // Combine search scope with search instructions for API request
        let combinedSearchInstructions = searchInstructions ?? ""
        if (isFullSiteSearch !== undefined) {
            const scopeInstruction = isFullSiteSearch
                ? `search_single_page=false`
                : `search_single_page=true`

            if (combinedSearchInstructions !== "") {
                combinedSearchInstructions = `${scopeInstruction}\n\n${combinedSearchInstructions}`
            } else {
                combinedSearchInstructions = scopeInstruction
            }
        }

        return {
            title: this.formFields.get("title"),
            knowledge_item_type: this.formFields.get("knowledge_item_type"),
            access_type: this.formFields.get("access_type"),
            access_group_id:
                this.formFields.get("access_group_id") ??
                DEFAULT_ACCESS_GROUP.id,
            content: this.formFields.get("content"),
            search_instructions: combinedSearchInstructions,
            tags: this.formFields.get("tags"),
            segments: this.formFields.get("segments"),
            agents: this.formFields.get("agents"),
            agent_instances: this.formFields.get("agent_instances"),
        }
    }

    getContent = async () => {
        if (this.id === null || this.id === undefined) {
            return
        }

        try {
            this.setIsLoading(true)
            const knowledgeItem =
                await KnowledgeHubAdminService.getV1AdminKnowledgebaseKnowledgeItems(
                    {
                        knowledgeItemId: this.id ?? 0,
                    },
                )

            this.formFields.set("content", knowledgeItem.content)
        } catch (error) {
            reportError(t`knowledge-item-modal.load-content-fail`, error)
        } finally {
            this.setIsLoading(false)
        }
    }

    getAgentTypeValue = (): IGenericAutoCompleteTextFieldValue[] => {
        const selectedAgentIds = this.formFields.get("agents") ?? []
        return this.agentAccessTypes.filter((type) =>
            selectedAgentIds.includes(type.id as number),
        )
    }

    setSelectedAgentTypes = async (
        agentTypes: IGenericAutoCompleteTextFieldValue[],
    ) => {
        this.formFields.set(
            "agents",
            agentTypes.map((agent) => Number(agent.id)),
        )

        for (const agentType of agentTypes) {
            if (agentType.label !== "" && agentType.label != null) {
                await this.fetchAgentInstances(agentType.label)
            }
        }
    }

    deselectAgentType = (agentTypeId: number) => {
        const currentAgents = this.getAgentTypeValue()
        const updatedAgents = currentAgents.filter(
            (agent) => agent.id !== agentTypeId,
        )
        this.formFields.set(
            "agents",
            updatedAgents.map((agent) => agent.id as number),
        )
    }

    private setId(id: number) {
        this.id = id
    }

    private parseSearchScopeFromInstructions(
        searchInstructions: string,
    ): boolean {
        return searchInstructions.includes("search_single_page=false")
    }

    private removeSearchScopeFromInstructions(
        searchInstructions: string,
    ): string {
        return searchInstructions
            .replace(/search_single_page=(true|false)\s*\n?\s*/g, "")
            .replace(/\n\s*\n/g, "\n")
            .trim()
    }
}
